import { ReactNode } from 'react';

import { V1CloudEventsPathSuggestion } from '@ariksa/audittrail/api';

export interface SelectOption {
  label: string;
  value: string;
  icon?: ReactNode;
  accountId?: string;
  bucketName?: string;
  depth?: number;
  parentValue?: string;
  descendantValues?: string[];
  isIndeterminate?: boolean;
}

export const flattenPaths = (
  paths: V1CloudEventsPathSuggestion[] = [],
  depth = 0,
  parentValue?: string,
): SelectOption[] =>
  paths.flatMap(path => {
    const value = String(path.relative_path ?? path.display_name ?? '');
    const children = flattenPaths(path.sub_folders ?? [], depth + 1, value);

    return value
      ? [
          {
            label: String(path.display_name ?? value),
            value,
            depth,
            parentValue,
            descendantValues: children.map(child => child.value),
          },
          ...children,
        ]
      : children;
  });

export const flattenSelectedPaths = (
  paths: V1CloudEventsPathSuggestion[] = [],
  depth = 0,
  parentValue?: string,
): SelectOption[] =>
  paths.flatMap(path => {
    const value = String(path.relative_path ?? path.display_name ?? '');
    const children = flattenSelectedPaths(
      path.sub_folders ?? [],
      depth + 1,
      value,
    );

    if (!value) return children;

    const option = {
      label: String(path.display_name ?? value),
      value,
      depth,
      parentValue,
      descendantValues: flattenPaths(
        path.sub_folders ?? [],
        depth + 1,
        value,
      ).map(child => child.value),
    };

    return children.length ? children : [option];
  });

export const getSelectedFolderPaths = (
  folders: SelectOption[],
  options: SelectOption[],
): string[] => {
  const optionsByValue = new Map(
    [...folders, ...options].map(option => [option.value, option]),
  );
  const selectedValues = new Set(folders.map(folder => folder.value));

  folders.forEach(folder => {
    optionsByValue
      .get(folder.value)
      ?.descendantValues?.forEach(value => selectedValues.add(value));
  });

  [...options]
    .sort((left, right) => (right.depth ?? 0) - (left.depth ?? 0))
    .forEach(option => {
      if (
        option.descendantValues?.length &&
        option.descendantValues.every(value => selectedValues.has(value))
      ) {
        selectedValues.add(option.value);
        option.descendantValues.forEach(value => selectedValues.delete(value));
      }
    });

  return [...selectedValues];
};

export const normalizeSelectedFolders = (
  folders: SelectOption[],
  options: SelectOption[],
) => {
  const optionsByValue = new Map(
    [...folders, ...options].map(option => [option.value, option]),
  );
  const selectedValues = new Set(folders.map(folder => folder.value));

  folders.forEach(folder => {
    optionsByValue
      .get(folder.value)
      ?.descendantValues?.forEach(value => selectedValues.add(value));
  });

  [...options]
    .sort((left, right) => (right.depth ?? 0) - (left.depth ?? 0))
    .forEach(option => {
      if (
        option.descendantValues?.length &&
        option.descendantValues.every(value => selectedValues.has(value))
      ) {
        selectedValues.add(option.value);
      }
    });

  return [...selectedValues]
    .map(value => optionsByValue.get(value))
    .filter(Boolean) as SelectOption[];
};

export const getSelectedFolderCount = (folders: SelectOption[]) => {
  const selectedValues = new Set(folders.map(folder => folder.value));

  return folders.filter(
    folder => !folder.parentValue || !selectedValues.has(folder.parentValue),
  ).length;
};

export const getFolderOptionsWithSelection = (
  options: SelectOption[],
  folders: SelectOption[],
) => {
  const selectedValues = new Set(folders.map(folder => folder.value));

  return options.map(option => ({
    ...option,
    isIndeterminate:
      !!option.descendantValues?.length &&
      option.descendantValues.some(value => selectedValues.has(value)) &&
      !option.descendantValues.every(value => selectedValues.has(value)),
  }));
};
